import L from 'leaflet';
import "leaflet.markercluster";
import utils from "./utils";

'use strict';

class SearchResultMap {

  constructor(elem) {
    this.elem = elem;
    this.map = null;
    this.markers = [];
    this.locations = [];
    this.mapBounds = null;

    this.bindUI();
  }

  /*
  |--------------------------------------------------------------------------
  | PUBLIC METHOD → Call this AFTER results injection
  |--------------------------------------------------------------------------
  */
  refresh() {
    this.initSelectors();
    this.initData();

    this.restoreActiveState();

    if (!this.locations.length) {
      console.warn('No locations found, map not initialized');
      return;
    }

    this.destroyMap();
    this.initMap();
  }

  /*
  |--------------------------------------------------------------------------
  | Selectors
  |--------------------------------------------------------------------------
  */
  initSelectors() {
    this.searchResultsList = document.querySelector('.search-results-list');
    this.searchResults = document.querySelector('.search-results');
    this.searchResultsListItem = document.querySelectorAll('.search-results-village');
  }

  /*
  |--------------------------------------------------------------------------
  | Data
  |--------------------------------------------------------------------------
  */
  initData() {
    this.locations = [];

    this.searchResultsListItem.forEach(village => {
      if (village.dataset.location) {
        this.locations.push(JSON.parse(village.dataset.location));
      }
    });
  }

  /*
  |--------------------------------------------------------------------------
  | Map Lifecycle
  |--------------------------------------------------------------------------
  */
  destroyMap() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  initMap() {
    const tiles = L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Points &copy 2012 LINZ',
        maxZoom: 20
      }
    );

    this.map = L.map('search-results-map', {
      zoom: 12,
      layers: [tiles]
    });

    const customIcon = L.icon({
      iconUrl: '/themes/siblu/dist/assets/svg/marker.svg',
      iconSize: [20, 30]
    });

    const customActiveIcon = L.icon({
      iconUrl: '/themes/siblu/dist/assets/svg/marker-active.svg',
      iconSize: [41, 62]
    });

    const markerClusterGroup = L.markerClusterGroup({
      disableClusteringAtZoom: 12
    });

    this.markers = [];

    this.locations.forEach(location => {

      const village = document.getElementById(location.id);
      if (!village) return;

      const marker = L.marker(
        [location.lat, location.lng],
        { icon: customIcon }
      );

      marker.id = location.id;

      // 🔹 Hover tooltip.
      marker.bindTooltip(
        '<div class="p-xs ta-c">' +
        '<h2 class="fs-xxs fw-600 m-no c-darker">' + location.title + '</h2>' +
        '<p class="fs-xxxs m-no fs-xxs@>md">' + location.price + '</p>' +
        '</div>',
        { direction: 'top', offset: [0, -10] }
      );

      // 🔹 Click event on marker.
      marker.on('click', () => {
        if (utils.mq.md.matches) {
          window.open(location.url, "_self");
        } else {
          this.activateMarker(marker, customIcon, customActiveIcon);
          village.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });

      // 🔹 Click event on village → enable marker.
      village.addEventListener('click', () => {
        this.activateMarker(marker, customIcon, customActiveIcon);
        this.map.setView(marker.getLatLng(), 12);
      });

      markerClusterGroup.addLayer(marker);
      this.markers.push(marker);
    });

    this.map.addLayer(markerClusterGroup);

    this.mapBounds = markerClusterGroup.getBounds();
    if (this.mapBounds.isValid()) {
      this.map.fitBounds(this.mapBounds, { padding: [30, 30] });
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Marker state
  |--------------------------------------------------------------------------
  */
  activateMarker(marker, defaultIcon, activeIcon) {
    // 🔹 Reset all markers and villages
    this.markers.forEach(m => {
      m.setIcon(defaultIcon);
      m.options.active = false;

      const villageEl = document.getElementById(m.id);
      if (villageEl) {
        villageEl.classList.remove('active');
      }
    });

    // 🔹 Enable clicked marker.
    marker.setIcon(activeIcon);
    marker.options.active = true;

    // 🔹 Add active class on corresponding village on list.
    const activeVillage = document.getElementById(marker.id);
    if (activeVillage) {
      activeVillage.classList.add('active');
    }
  }

  /*
  |--------------------------------------------------------------------------
  | UI Bindings
  |--------------------------------------------------------------------------
  */
  bindUI() {
    document.addEventListener('reloadSearchResultMap', () => {
      this.refresh();
    });

    document.addEventListener('click', (e) => {

      const toggleBtn = e.target.closest('.js-search-results-map-toggle');

      if (toggleBtn) {
        e.preventDefault();
        this.handleToggleClick(toggleBtn);
      }

    });
  }

  handleToggleClick(btn) {
    this.initSelectors();
    if (!this.elem.classList.contains('active')) {
      const resultsPage = this.searchResults.closest('.search-results-page');

      btn.classList.toggle('active');
      btn.classList.remove('cta-icon-map');
      btn.classList.toggle('cta-icon-list');
      btn.textContent = Drupal.t('View list');
      this.elem.classList.toggle('active');
      this.searchResultsList.classList.toggle('active');
      this.searchResults.classList.toggle('active');

      resultsPage.dataset.activeViewMode =
        this.elem.classList.contains('active') ? 'map' : 'list';

      if (this.elem.classList.contains('active')) {
        if (!this.map) {
          this.waitForVillagesThenRefresh();
        } else {
          setTimeout(() => {
            this.map.invalidateSize();
            if (this.mapBounds?.isValid()) {
              this.map.fitBounds(this.mapBounds, { padding: [30, 30] });
            }
          }, 200);
        }
      }
    } else {
      btn.classList.remove('cta-icon-list');
      btn.classList.toggle('cta-icon-map');
      btn.textContent = Drupal.t('View map');
      this.elem.classList.remove('active');
      this.searchResultsList.classList.remove('active');
      this.searchResults.classList.remove('active');

      const resultsPage = this.searchResults.closest('.search-results-page');
      resultsPage.dataset.activeViewMode = 'list';
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Wait for AJAX / BigPipe
  |--------------------------------------------------------------------------
  */
  waitForVillagesThenRefresh() {
    const container = document.querySelector('#villages-container');

    if (!container) return;

    if (container.querySelectorAll('.search-results-village').length) {
      this.refresh();
      return;
    }

    const observer = new MutationObserver(() => {
      const villages = container.querySelectorAll('.search-results-village');
      if (villages.length) {
        observer.disconnect();
        requestAnimationFrame(() => {
          this.refresh();
        });
      }
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  /*
  |--------------------------------------------------------------------------
  | Restore map/list active state
  |--------------------------------------------------------------------------
  */
  restoreActiveState() {
    const resultsPage = this.searchResults.closest('.search-results-page');
    if (!resultsPage) return;

    if (resultsPage.dataset.activeViewMode === 'map') {
      this.elem.classList.add('active');
      this.searchResultsList.classList.add('active');
      this.searchResults.classList.add('active');
    } else {
      this.elem.classList.remove('active');
      this.searchResultsList.classList.remove('active');
      this.searchResults.classList.remove('active');
    }
  }

}

/*
|--------------------------------------------------------------------------
| INIT Drupal behavior
|--------------------------------------------------------------------------
*/
(function (Drupal, once) {

  Drupal.behaviors.searchResultMapBehavior = {
    attach(context) {

      once('search-result-map', context.querySelectorAll('.js-search-results-map'))
        .forEach((el) => {
          const instance = new SearchResultMap(el);
          instance.waitForVillagesThenRefresh();
        });
    }
  };

})(Drupal, once);
